<template>
  <div
    ref="mapContainer"
    class="full-width google-map-height"
    :class="confirmedPageDesktop ? 'confirmed-page-map-height' : ''"
  >
    <div v-if="loading" class="loading-overlay">Loading map...</div>
    <div v-if="error" class="error-overlay">{{ error }}</div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import GoogleMapsLoader from '@/services/GoogleMapsLoader';

export default {
  name: 'GoogleMap',
  async mounted() {
    await this.initMap();
  },
  beforeDestroy() {
    if (this.marker) {
      this.marker.setMap(null);
      this.marker = null;
    }

    this.map = null;
  },
  computed: {
    ...mapState({
      storeLat: state => parseFloat(state.store?.info.lat) || 0,
      storeLng: state => parseFloat(state.store?.info.lng) || 0,
      storeName: state => state.store?.info.name,
      address: state => (state.store && state.store.info ? state.store.info.singleline : null),
      storeImage: state => state.store?.info.image,
      customerAddress: state => state.orderStatus.localOrderAddress,
      bringgOrderStep: state => state.orderStatus.bringgOrderResponse.orderStep,
    }),
    formattedAddress() {
      if (this.customerAddress && this.bringgOrderStep === 4) {
        return this.customerAddress.replace(', USA', '');
      }
      return this.address ? this.address.replace(', USA', '') : '';
    },
    lat() {
      if (this.bringgOrderStep !== 4) return this.storeLat;
      return this.customerLat;
    },
    lng() {
      if (this.bringgOrderStep !== 4) return this.storeLng;
      return this.customerLng;
    },
    markerIcon() {
      return this.$route.name === 'confirmed'
        ? this.storeImage
        : require('@/assets/spotlight-pin.png');
    },
    markerLabel() {
      return `${this.storeName}\n${this.formattedAddress}`;
    },
  },
  data() {
    return {
      map: null,
      marker: null,
      loading: true,
      error: null,
    };
  },
  methods: {
    async initMap() {
      this.loading = true;
      this.error = null;

      try {
        const mapsLib = await GoogleMapsLoader.loadLibrary('maps');
        const { Map } = mapsLib;

        this.map = new Map(this.$refs.mapContainer, {
          center: { lat: this.lat, lng: this.lng },
          zoom: 16,
          disableDefaultUI: true,
        });

        await this.createMarker();

        this.loading = false;
      } catch (error) {
        this.error = 'Failed to load map';
        this.loading = false;
        console.error('Map initialization failed:', error);
      }
    },
    async createMarker() {
      try {
        const position = { lat: this.lat, lng: this.lng };

        const iconSize =
          this.$route.name === 'confirmed' ? new window.google.maps.Size(29, 29) : null;

        this.marker = new window.google.maps.Marker({
          position,
          map: this.map,
          icon: {
            url: this.markerIcon + '#custom_marker',
            labelOrigin: new window.google.maps.Point(15, -45),
            ...(iconSize && { scaledSize: iconSize }),
          },
          label: {
            text: this.markerLabel,
            className: 'custom-label',
          },
          clickable: false,
          draggable: false,
        });
      } catch (error) {
        console.error('Failed to create marker:', error);
      }
    },
    panToLocation(newLat, newLng) {
      if (!this.map) return;

      const newPosition = new window.google.maps.LatLng(newLat, newLng);
      this.map.panTo(newPosition);
    },
    updateMarker() {
      if (!this.marker || !this.map) return;

      const position = { lat: this.lat, lng: this.lng };

      this.marker.setPosition(position);

      const iconSize =
        this.$route.name === 'confirmed' ? new window.google.maps.Size(29, 29) : null;

      this.marker.setIcon({
        url: this.markerIcon + '#custom_marker',
        labelOrigin: new window.google.maps.Point(15, -45),
        ...(iconSize && { scaledSize: iconSize }),
      });

      this.marker.setLabel({
        text: this.markerLabel,
        className: 'custom-label',
      });

      this.panToLocation(this.lat, this.lng);
    },
  },
  props: {
    confirmedPageDesktop: Boolean,
    customerLat: Number,
    customerLng: Number,
  },
  watch: {
    bringgOrderStep: function (newStep) {
      if (newStep !== 4) return;
      this.panToLocation(this.lat, this.lng);
      this.updateMarker();
    },
    lat() {
      if (this.marker && this.map) {
        this.updateMarker();
      }
    },
    lng() {
      if (this.marker && this.map) {
        this.updateMarker();
      }
    },
    markerIcon() {
      if (this.marker) {
        this.updateMarker();
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.google-map-height {
  height: 100%;
  position: relative;
}

.confirmed-page-map-height {
  height: 560px;
}

.loading-overlay,
.error-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.error-overlay {
  color: #d32f2f;
}

:deep(img[src$="#custom_marker"]) {
  border: 3px solid var(--v-secondary-base) !important;
  border-radius: 50%;
}

:deep(.custom-label) {
  color: white !important;
  white-space: break-spaces;
  background-color: var(--v-secondary-base);
  border-radius: 8px;
  padding: 4px;
  position: relative;
  box-shadow: #00000050 0 0 10px 2px;
}

:deep(.custom-label)::after {
  content: "";
  position: absolute;
  top: 119%;
  left: 50%;
  margin-left: -8px;
  border-width: 8px;
  border-style: solid;
  border-color: var(--v-secondary-base) transparent transparent transparent;
  scale: 1 2;
}
</style>
